import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  FaWeightHanging, FaSearchPlus, FaUserGraduate, FaRulerCombined,
  FaPuzzlePiece, FaComments, FaShieldAlt, FaCheck, FaTimes,
  FaChevronDown, FaChevronRight, FaCode, FaFlask
} from 'react-icons/fa'

/**
 * WeightResult — visualizes the output of the `weight_v1` Play.
 *
 * Expects `data` to be the contents of `analyses.weight_v1.data` produced
 * by `WeightPlay._to_play_result()` in the backend (see ocrapp/plays/weight.py).
 *
 * Styled to match the other plays (light cards, amber executive summary)
 * and structured as a step-by-step narrative of how SourceOptima Agentic AI
 * arrived at the final mass.
 */
export default function WeightResult({ data, success, error, metadata }) {
  if (!data) {
    return (
      <div className="p-6 text-gray-500">No weight estimation data available for this file.</div>
    )
  }

  const {
    estimated_kg,
    fill_factor_pct,
    material,
    density_g_cm3,
    net_volume_cm3,
    classification = {},
    bounding_box = {},
    iterations,
    terminal_status,
    acceptance_gates = {},
    time_seconds,
    reclassified,
    auto_generated_specialist,
    decomposition_table,
    calculation_steps,
    python_code,
    sanity_check,
    component_description,
    corrections_made,
    debate_rounds,
    debate_confidence,
    confidence,
    confidence_rationale,
  } = data

  const isNum = (n) => typeof n === 'number' && Number.isFinite(n)
  const formatNum = (n, decimals = 2) =>
    isNum(n) ? n.toFixed(decimals) : '—'
  const formatPct = (n, decimals = 0) =>
    isNum(n) ? `${(n * 100).toFixed(decimals)}%` : null

  const confidenceStyle = {
    high:   'bg-green-100 text-green-800 border-green-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low:    'bg-red-100 text-red-800 border-red-200',
  }[confidence] || 'bg-gray-100 text-gray-700 border-gray-200'

  const statusStyle = success
    ? 'bg-green-100 text-green-800 border-green-200'
    : 'bg-red-100 text-red-800 border-red-200'

  const gatesPassed = Object.values(acceptance_gates).filter(Boolean).length
  const gatesTotal = Object.keys(acceptance_gates).length

  // Build the story steps dynamically so numbering stays sequential even when
  // optional steps (geometry, decomposition) are skipped.
  const hasGeometry = !!(bounding_box.length_mm || bounding_box.width_mm || bounding_box.height_mm)
  const hasDecomposition = Array.isArray(decomposition_table) && decomposition_table.length > 0
  const hasGates = gatesTotal > 0
  const bboxSourceShort = (() => {
    const src = bounding_box.source
    if (!src || typeof src !== 'string') return null
    // Strip common prose openings to get a short tag
    const cleaned = src.replace(/^(derived\s+from|from|via|using)\s+/i, '').trim()
    return cleaned.length > 28 ? null : cleaned
  })()

  return (
    <div className="space-y-6">

      {/* ─────────────────────────────────────────────────────────────
          HERO — Executive Summary
          ───────────────────────────────────────────────────────────── */}
      <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6 bg-amber-50">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-white shadow-sm">
              <FaWeightHanging className="w-6 h-6 text-[#1e5045]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Estimated Mass
              </h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-[#1e5045]">
                  {formatNum(estimated_kg, 3)}
                </span>
                <span className="text-xl text-gray-500">kg</span>
              </div>
              {material && (
                <p className="text-sm text-gray-600 mt-1">
                  {material}
                  {density_g_cm3 != null && (
                    <span className="text-gray-500"> · {formatNum(density_g_cm3, 3)} g/cm³</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusStyle}`}>
              {terminal_status || (success ? 'accepted' : 'rejected')}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${confidenceStyle}`}>
              {(confidence || 'unknown').toUpperCase()} confidence
            </span>
          </div>
        </div>

        {confidence_rationale && (
          <p className="text-sm text-gray-700 italic mt-4 pl-1">
            "{confidence_rationale}"
          </p>
        )}

        {/* Quick stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <SummaryStat label="Iterations" value={iterations ?? '—'} accent="text-[#1e5045]" />
          <SummaryStat
            label="Debate Rounds"
            value={debate_rounds ?? 0}
            accent="text-purple-700"
          />
          <SummaryStat
            label="Acceptance Gates"
            value={gatesTotal ? `${gatesPassed}/${gatesTotal}` : '—'}
            accent={gatesPassed === gatesTotal && gatesTotal > 0 ? 'text-green-700' : 'text-amber-700'}
          />
          <SummaryStat
            label="Total Time"
            value={time_seconds != null ? `${formatNum(time_seconds, 1)} s` : '—'}
            accent="text-[#f26522]"
          />
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          THE STORY — How SourceOptima Agentic AI got here
          ───────────────────────────────────────────────────────────── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          How SourceOptima Agentic AI arrived at this number
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Each step is a real decision the agent made.
        </p>

        <NumberedSteps>

          {/* Step — Classification */}
          <StoryStep
            icon={<FaSearchPlus className="w-4 h-4" />}
            title="Classified the drawing"
            summary={
              classification.topology || classification.category
                ? `Identified as ${[classification.topology, classification.category].filter(Boolean).join(' · ')}`
                : 'Routed the drawing through the classifier'
            }
            badge={
              isNum(classification.classifier_confidence)
                ? `${(classification.classifier_confidence * 100).toFixed(0)}% sure`
                : null
            }
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <KV k="Topology" v={classification.topology} />
              <KV k="Part category" v={classification.category} />
              <KV k="Material family" v={classification.material_family} />
              <KV k="Manufacturing" v={classification.manufacturing_method} />
              <KV
                k="Material on drawing?"
                v={classification.material_stated ? 'Yes — picked up directly' : 'No — inferred from context'}
              />
              {classification.stated_weight_kg != null && (
                <KV
                  k="Stated weight (reference)"
                  v={`${formatNum(classification.stated_weight_kg, 3)} kg`}
                />
              )}
            </div>
            {reclassified && (
              <NarrativeNote tone="amber">
                Mid-pipeline the agent reclassified this part — its first guess didn't pass
                scrutiny, so it re-examined the drawing and routed to a different specialist.
              </NarrativeNote>
            )}
          </StoryStep>

          {/* Step — Specialist */}
          <StoryStep
            icon={<FaUserGraduate className="w-4 h-4" />}
            title="Picked a specialist"
            summary={
              classification.specialist_used
                ? `Routed to "${classification.specialist_used}"`
                : 'Selected a domain specialist'
            }
            badge={auto_generated_specialist ? 'Auto-generated' : null}
          >
            <p className="text-sm text-gray-600">
              {auto_generated_specialist ? (
                <>
                  This part didn't match any pre-built specialist, so the agent{' '}
                  <strong>generated a brand new specialist profile</strong> on the fly —
                  calibrated to this drawing's geometry and manufacturing method.
                </>
              ) : (
                <>
                  The classifier picked an existing specialist whose calibration matches this
                  part's topology and manufacturing method. Specialists own the calculation
                  prompt, the typical fill factor range, and known sanity bounds.
                </>
              )}
            </p>
          </StoryStep>

          {/* Step — Geometry */}
          {hasGeometry && (
            <StoryStep
              icon={<FaRulerCombined className="w-4 h-4" />}
              title="Read the geometry"
              summary={`Bounding box ${formatNum(bounding_box.length_mm, 0)} × ${formatNum(bounding_box.width_mm, 0)} × ${formatNum(bounding_box.height_mm, 0)} mm`}
              badge={bboxSourceShort}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <KV k="Length / Ø" v={`${formatNum(bounding_box.length_mm, 1)} mm`} />
                <KV k="Width" v={`${formatNum(bounding_box.width_mm, 1)} mm`} />
                <KV k="Height" v={`${formatNum(bounding_box.height_mm, 1)} mm`} />
              </div>
              {bounding_box.source && (
                <p className="text-xs text-gray-500 italic mt-3">
                  Source: {bounding_box.source}
                </p>
              )}
              {net_volume_cm3 != null && (
                <p className="text-sm text-gray-600 mt-3">
                  After decomposing the part into primitives and subtracting pockets/holes,
                  the net solid volume came out to <strong>{formatNum(net_volume_cm3, 1)} cm³</strong>
                  {fill_factor_pct != null && (
                    <> — that's a <strong>{formatNum(fill_factor_pct, 1)}% fill factor</strong> against the bounding box.</>
                  )}
                </p>
              )}
            </StoryStep>
          )}

          {/* Step — Decomposition */}
          {hasDecomposition && (
            <StoryStep
              icon={<FaPuzzlePiece className="w-4 h-4" />}
              title="Decomposed into primitives"
              summary={`Broke the part into ${decomposition_table.length} primitive ${decomposition_table.length === 1 ? 'shape' : 'shapes'}`}
              badge={`${decomposition_table.length} parts`}
            >
              <p className="text-sm text-gray-600 mb-3">
                The specialist's calculation prompt told the agent how to slice this kind of
                part. Each row below is a primitive whose volume the agent computed
                independently — they're summed (with subtractions for pockets/cutouts) to get
                the net volume.
              </p>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(decomposition_table[0]).map((col) => (
                        <th
                          key={col}
                          className="text-left py-2 px-3 capitalize font-semibold text-gray-700 border-b border-gray-200"
                        >
                          {col.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {decomposition_table.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-gray-100 last:border-b-0 hover:bg-amber-50/40"
                      >
                        {Object.values(row).map((cell, j) => (
                          <td key={j} className="py-2 px-3 text-gray-800">
                            {typeof cell === 'number' ? cell.toFixed(3) : String(cell ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </StoryStep>
          )}

          {/* Step — Adversarial Debate */}
          <StoryStep
            icon={<FaComments className="w-4 h-4" />}
            title="Adversarial debate"
            summary={
              isNum(debate_rounds) && debate_rounds > 0
                ? `Calculator vs. Reviewer over ${debate_rounds} ${debate_rounds === 1 ? 'round' : 'rounds'}`
                : 'Calculator and reviewer agreed on first pass'
            }
            badge={formatPct(debate_confidence) ? `${formatPct(debate_confidence)} agreement` : null}
          >
            <p className="text-sm text-gray-600">
              In Phase B the agent runs an internal debate: a <strong>Calculator</strong>{' '}
              persona produces a mass, then a <strong>Reviewer</strong> persona scrutinizes
              the geometry, decomposition, and density choice. They iterate until they
              converge — or the reviewer accepts.
            </p>
            {corrections_made != null && corrections_made > 0 && (
              <NarrativeNote tone="amber">
                The reviewer caught{' '}
                <strong>
                  {corrections_made} {corrections_made === 1 ? 'correction' : 'corrections'}
                </strong>{' '}
                during the debate — typical issues are missed pockets, wrong material density,
                or mis-applied fill factor.
              </NarrativeNote>
            )}
            {(!corrections_made || corrections_made === 0) && (
              <NarrativeNote tone="green">
                The reviewer didn't push back on the calculator's first answer — both
                personas converged on the same number without modifications.
              </NarrativeNote>
            )}
          </StoryStep>

          {/* Step — Acceptance Gates */}
          {hasGates && (
            <StoryStep
              icon={<FaShieldAlt className="w-4 h-4" />}
              title="Final acceptance gates"
              summary={`${gatesPassed} of ${gatesTotal} gates passed`}
              badge={gatesPassed === gatesTotal ? 'All clear' : 'Failed'}
              badgeTone={gatesPassed === gatesTotal ? 'green' : 'red'}
            >
              <p className="text-sm text-gray-600 mb-3">
                Before any number is returned, it has to clear deterministic safety gates that
                catch obvious mistakes (parsing errors, fill factor outside the specialist's
                expected range, implausible mass for the bounding volume, bounding-box
                inconsistencies).
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(acceptance_gates).map(([gate, ok]) => (
                  <div
                    key={gate}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                      ok
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}
                  >
                    {ok ? <FaCheck className="w-3 h-3" /> : <FaTimes className="w-3 h-3" />}
                    <span className="capitalize">{gate.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </StoryStep>
          )}

        </NumberedSteps>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          KEY NUMBERS — at-a-glance reference
          ───────────────────────────────────────────────────────────── */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Numbers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KeyNumberCard label="Material" value={material || '—'} />
          <KeyNumberCard
            label="Density"
            value={density_g_cm3 != null ? `${formatNum(density_g_cm3, 3)} g/cm³` : '—'}
          />
          <KeyNumberCard
            label="Net Volume"
            value={net_volume_cm3 != null ? `${formatNum(net_volume_cm3, 1)} cm³` : '—'}
          />
          <KeyNumberCard
            label="Fill Factor"
            value={fill_factor_pct != null ? `${formatNum(fill_factor_pct, 1)}%` : '—'}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          REASONING — always visible
          ─────────────────────────────────────────────────────────── */}
      {(calculation_steps || component_description) && (
        <div className="rounded-xl shadow-sm border border-gray-100 bg-white overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
            <FaFlask className="w-4 h-4 text-[#1e5045]" />
            <h3 className="text-sm font-semibold text-gray-900">
              Calculation reasoning (agent's own words)
            </h3>
          </div>
          <div className="px-4 py-4 prose prose-sm max-w-none text-gray-700">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {String(calculation_steps || component_description)}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {sanity_check && hasSanityContent(sanity_check) && (
        <Collapsible
          title="Sanity check"
          icon={<FaShieldAlt className="w-4 h-4 text-[#1e5045]" />}
        >
          <div className="text-sm text-gray-700 whitespace-pre-wrap">
            {typeof sanity_check === 'string'
              ? sanity_check
              : JSON.stringify(sanity_check, null, 2)}
          </div>
        </Collapsible>
      )}

      {/* ─────────────────────────────────────────────────────────────
          PYTHON CODE — collapsed by default
          ───────────────────────────────────────────────────────────── */}
      {Array.isArray(python_code) && python_code.length > 0 && (
        <Collapsible
          title={`Python code (${python_code.length} ${python_code.length === 1 ? 'block' : 'blocks'} from final iteration)`}
          icon={<FaCode className="w-4 h-4 text-[#1e5045]" />}
        >
          <div className="space-y-3">
            {python_code.map((block, i) => (
              <pre
                key={i}
                className="p-3 rounded-lg bg-gray-900 text-xs text-emerald-300 overflow-x-auto font-mono"
              >
                {typeof block === 'string' ? block : JSON.stringify(block, null, 2)}
              </pre>
            ))}
          </div>
        </Collapsible>
      )}

      {/* ─────────────────────────────────────────────────────────────
          METADATA — small footer
          ───────────────────────────────────────────────────────────── */}
      {metadata && (
        <div className="text-xs text-gray-500 px-1 space-y-1">
          {metadata.models_used && (
            <div>AI Engine: {metadata.models_used.join(' + ')}</div>
          )}
          {metadata.specialist && <div>Specialist: {metadata.specialist}</div>}
        </div>
      )}
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────────
// Presentational helpers
// ───────────────────────────────────────────────────────────────────────────

function SummaryStat({ label, value, accent = 'text-gray-900' }) {
  return (
    <div className="text-center p-3 bg-white rounded-lg">
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  )
}

function KeyNumberCard({ label, value }) {
  return (
    <div className="rounded-xl shadow-sm border border-gray-100 p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-1 truncate" title={value}>
        {value}
      </div>
    </div>
  )
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{k}</div>
      <div className="text-gray-900 font-medium mt-0.5">
        {v != null && v !== '' ? String(v) : '—'}
      </div>
    </div>
  )
}

function hasSanityContent(s) {
  if (!s) return false
  if (typeof s === 'string') return s.trim().length > 0
  if (typeof s === 'object') return Object.keys(s).length > 0
  return true
}

function NumberedSteps({ children }) {
  // Renumber children sequentially so optional steps don't leave gaps
  const visible = React.Children.toArray(children).filter(Boolean)
  return (
    <div className="space-y-3">
      {visible.map((child, i) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { number: i + 1 })
          : child
      )}
    </div>
  )
}

function StoryStep({ number, icon, title, summary, badge, badgeTone = 'gray', children }) {
  const badgeClasses = {
    gray:  'bg-gray-100 text-gray-700',
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red:   'bg-red-100 text-red-800',
  }[badgeTone] || 'bg-gray-100 text-gray-700'

  return (
    <div className="rounded-xl shadow-sm border border-gray-100 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1e5045] text-white flex items-center justify-center text-sm font-semibold">
          {number}
        </span>
        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-50 text-[#f26522] flex items-center justify-center">
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-600">{summary}</div>
        </div>
        {badge && (
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClasses}`}>
            {badge}
          </span>
        )}
      </div>
      <div className="px-4 py-4 border-t border-gray-100 bg-gray-50/40">
        {children}
      </div>
    </div>
  )
}

function NarrativeNote({ tone = 'gray', children }) {
  const toneClasses = {
    green: 'bg-green-50 border-green-200 text-green-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    gray:  'bg-gray-50 border-gray-200 text-gray-800',
  }[tone] || 'bg-gray-50 border-gray-200 text-gray-800'

  return (
    <div className={`mt-3 p-3 rounded-lg border text-sm ${toneClasses}`}>
      {children}
    </div>
  )
}

function Collapsible({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-xl shadow-sm border border-gray-100 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        {icon}
        <span className="flex-1 text-sm font-semibold text-gray-900">{title}</span>
        <span className="text-gray-400">
          {open ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
        </span>
      </button>
      {open && (
        <div className="px-4 py-4 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  )
}
